import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import {
  queueApplicationFormNotify as queueNotify,
  type ApplicationNotifyResult,
  type ApplicationNotifyEvent,
} from './applicationFormSettings';
import {
  canSendApplicationFormEmailViaPortal,
  sendApplicationFormEventViaPortal,
} from './applicationFormPortalEmail';
import type {
  AccountCreationAttachmentRef,
  AccountCreationFinanceData,
  AccountCreationFormData,
  ApplicationSubmission,
  ApplicationSubmissionSource,
  ApplicationSubmissionStatus,
} from '../types/applicationForm';
import { DEFAULT_ACCOUNT_CREATION_FINANCE } from '../types/applicationForm';
import { isValidPaymentTerms } from './accountCreationUi';

const ACRA_BUCKET = 'application-form-attachments';
const ACRA_MAX_BYTES = 10 * 1024 * 1024;
const ACRA_ALLOWED_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

function sanitizeAttachmentName(name: string): string {
  const trimmed = name.trim() || 'acra-upload';
  return trimmed.replace(/[^\w.\-()+ ]/g, '_').slice(0, 120);
}

export function validateAcraAttachment(file: File): string | null {
  if (!file || file.size === 0) return null;
  if (file.size > ACRA_MAX_BYTES) return 'Attachment must be 10 MB or smaller.';
  if (file.type && !ACRA_ALLOWED_TYPES.has(file.type)) {
    return 'Attachment must be a PDF or image (JPEG, PNG, WebP).';
  }
  return null;
}

export function normalizeAcraAttachments(
  formData: Pick<
    AccountCreationFormData,
    'acra_attachments' | 'acra_attachment_path' | 'acra_attachment_name'
  >,
): AccountCreationAttachmentRef[] {
  const listed = Array.isArray(formData.acra_attachments)
    ? formData.acra_attachments.filter((row) => row?.path?.trim())
    : [];
  if (listed.length > 0) {
    return listed.map((row) => ({
      path: row.path.trim(),
      name: row.name?.trim() || row.path.split('/').pop() || 'ACRA',
    }));
  }
  const legacyPath = formData.acra_attachment_path?.trim();
  if (!legacyPath) return [];
  return [
    {
      path: legacyPath,
      name: formData.acra_attachment_name?.trim() || legacyPath.split('/').pop() || 'ACRA',
    },
  ];
}

export function normalizeSupportingAttachments(
  formData: Pick<AccountCreationFormData, 'supporting_attachments'>,
): AccountCreationAttachmentRef[] {
  const listed = Array.isArray(formData.supporting_attachments)
    ? formData.supporting_attachments.filter((row) => row?.path?.trim())
    : [];
  return listed.map((row) => ({
    path: row.path.trim(),
    name: row.name?.trim() || row.path.split('/').pop() || 'Document',
  }));
}

export async function uploadAcraAttachment(
  file: File,
  source: ApplicationSubmissionSource = 'public',
  folderHint: 'acra' | 'supporting' = 'acra',
): Promise<{ path: string; name: string }> {
  const issue = validateAcraAttachment(file);
  if (issue) throw new Error(issue);

  const db = getDbSupabase('forms');
  const clientKey = crypto.randomUUID();
  const safeName = sanitizeAttachmentName(file.name);
  const root = source === 'internal' ? 'internal' : 'public';
  const path = `${root}/${folderHint}/${clientKey}/${safeName}`;

  const { error } = await db.storage.from(ACRA_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });

  if (error) throw new Error(formatSupabaseError(error, 'storage'));
  return { path, name: file.name.trim() || safeName };
}

export async function getAcraAttachmentSignedUrl(path: string): Promise<string | null> {
  const clean = path.trim();
  if (!clean) return null;
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from(ACRA_BUCKET).createSignedUrl(clean, 3600);
  if (error) throw new Error(formatSupabaseError(error, 'storage'));
  return data?.signedUrl ?? null;
}

export { queueApplicationFormNotify, type ApplicationNotifyResult } from './applicationFormSettings';

async function notifyApplicationFormEvent(
  submission: ApplicationSubmission,
  event: ApplicationNotifyEvent,
): Promise<ApplicationNotifyResult> {
  if (canSendApplicationFormEmailViaPortal()) {
    return sendApplicationFormEventViaPortal(submission, event);
  }
  return queueNotify(submission.id, event);
}

export type AccountCreationSubmitResult = {
  submission: ApplicationSubmission;
  notify: ApplicationNotifyResult;
};

export type AccountCreationPendingFiles = {
  acra?: File[];
  supporting?: File[];
};

export async function submitAccountCreation(
  companyName: string,
  formData: AccountCreationFormData,
  source: ApplicationSubmissionSource,
  submittedBy?: string,
  pendingFiles?: AccountCreationPendingFiles | File | null,
  submitterEmail?: string,
): Promise<AccountCreationSubmitResult> {
  // Backward compatible: single File used to mean one ACRA upload.
  const files: AccountCreationPendingFiles =
    pendingFiles instanceof File || pendingFiles == null
      ? { acra: pendingFiles && pendingFiles.size > 0 ? [pendingFiles] : [], supporting: [] }
      : {
          acra: pendingFiles.acra ?? [],
          supporting: pendingFiles.supporting ?? [],
        };

  let payload: AccountCreationFormData = {
    ...formData,
    acra_attachments: normalizeAcraAttachments(formData),
    supporting_attachments: normalizeSupportingAttachments(formData),
  };

  if ((files.acra?.length ?? 0) > 0) {
    const uploaded = [];
    for (const file of files.acra ?? []) {
      if (!file || file.size <= 0) continue;
      uploaded.push(await uploadAcraAttachment(file, source, 'acra'));
    }
    const merged = [...payload.acra_attachments, ...uploaded];
    payload = {
      ...payload,
      acra_attachments: merged,
      acra_attachment_path: merged[0]?.path ?? '',
      acra_attachment_name: merged[0]?.name ?? '',
    };
  }

  if ((files.supporting?.length ?? 0) > 0) {
    const uploaded = [];
    for (const file of files.supporting ?? []) {
      if (!file || file.size <= 0) continue;
      uploaded.push(await uploadAcraAttachment(file, source, 'supporting'));
    }
    payload = {
      ...payload,
      supporting_attachments: [...payload.supporting_attachments, ...uploaded],
    };
  }

  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('submit_account_creation', {
    p_company_name: companyName.trim(),
    p_form_data: payload,
    p_source: source,
    p_submitted_by: submittedBy?.trim() || null,
    p_submitter_email: submitterEmail?.trim() || null,
  });

  if (error) throw new Error(formatSupabaseError(error));
  const submission: ApplicationSubmission = {
    ...(data as ApplicationSubmission),
    form_slug: 'account-creation',
  };
  const notify = await notifyApplicationFormEvent(submission, 'submission');
  if (!notify.ok || (notify.sent?.length ?? 0) === 0) {
    console.warn('Application submission emails:', notify);
  }
  return { submission, notify };
}

export async function fetchAccountCreationSubmission(id: string): Promise<ApplicationSubmission | null> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from('account_creation_submissions')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return { ...(data as ApplicationSubmission), form_slug: 'account-creation' };
}

export async function fetchAccountCreationSubmissions(): Promise<ApplicationSubmission[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from('account_creation_submissions')
    .select('*')
    .order('submitted_at', { ascending: false });

  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => ({
    ...(row as ApplicationSubmission),
    form_slug: 'account-creation' as const,
  }));
}

export type FinanceReviewAction = 'verified' | 'approved' | 'rejected' | 'kiv';

export type FinanceReviewer = {
  userId?: string | null;
  username?: string | null;
};

export type FinanceReviewInput = {
  customerCode?: string;
  reviewNotes?: string;
  financeData?: AccountCreationFinanceData;
  /** Optional form patch (verifier can correct applicant data; AR limited fields). */
  formData?: AccountCreationFormData;
  companyName?: string;
};

function normalizeFinanceData(
  raw: AccountCreationFinanceData,
  customerCode = '',
): AccountCreationFinanceData {
  const paymentTerms = isValidPaymentTerms(raw.payment_terms) ? raw.payment_terms : '';
  const code = customerCode.trim();
  return {
    ...DEFAULT_ACCOUNT_CREATION_FINANCE,
    ...raw,
    mother_account_code: code || raw.mother_account_code?.trim() || '',
    outlet_code: code || raw.outlet_code?.trim() || '',
    division_code: '10',
    payment_terms: paymentTerms,
    session: raw.session?.trim().toUpperCase() || 'AM',
    nav_card: raw.nav_card && typeof raw.nav_card === 'object' ? { ...raw.nav_card } : {},
  };
}

export async function updateAccountCreationFormData(
  id: string,
  companyName: string,
  formData: AccountCreationFormData,
): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db
    .from('account_creation_submissions')
    .update({
      company_name: companyName.trim(),
      form_data: formData,
    })
    .eq('id', id)
    .eq('status', 'pending_review');
  if (error) throw new Error(formatSupabaseError(error));
}

export async function updateSubmissionStatus(
  id: string,
  status: ApplicationSubmissionStatus | 'verified',
  input?: FinanceReviewInput,
  reviewer?: FinanceReviewer,
): Promise<void> {
  const customerCode = input?.customerCode?.trim() ?? '';
  const reviewNotes = input?.reviewNotes?.trim() ?? '';
  const nextStatus: ApplicationSubmissionStatus =
    status === 'verified' ? 'pending_approval' : status;

  if ((status === 'verified' || status === 'approved') && !customerCode) {
    throw new Error('Customer code is required.');
  }
  if (status === 'rejected' && !reviewNotes) {
    throw new Error('Rejection reason is required.');
  }

  const db = getDbSupabase('forms');
  const payload: Record<string, unknown> = {
    status: nextStatus,
    reviewed_at: new Date().toISOString(),
    review_notes: reviewNotes || null,
  };

  const reviewerId = reviewer?.userId?.trim();
  const reviewerUsername = reviewer?.username?.trim();
  if (reviewerId) payload.reviewed_by = reviewerId;
  if (reviewerUsername) payload.reviewed_by_username = reviewerUsername;

  if (status === 'verified') {
    payload.customer_code = customerCode;
    payload.finance_data = normalizeFinanceData(
      input?.financeData ?? DEFAULT_ACCOUNT_CREATION_FINANCE,
      customerCode,
    );
    if (input?.formData) payload.form_data = input.formData;
    if (input?.companyName?.trim()) payload.company_name = input.companyName.trim();
  }

  if (status === 'approved') {
    payload.customer_code = customerCode;
    const existing = await fetchAccountCreationSubmission(id);
    const baseFinance = existing?.finance_data ?? DEFAULT_ACCOUNT_CREATION_FINANCE;
    const mergedFinance = normalizeFinanceData(
      {
        ...baseFinance,
        ...(input?.financeData ?? {}),
        payment_terms:
          input?.financeData?.payment_terms || baseFinance.payment_terms || '',
      },
      customerCode || existing?.customer_code || '',
    );
    payload.finance_data = mergedFinance;
    if (input?.formData) payload.form_data = input.formData;
    if (input?.companyName?.trim()) payload.company_name = input.companyName.trim();
  }

  const { data, error } = await db
    .from('account_creation_submissions')
    .update(payload)
    .eq('id', id)
    .select('id,status')
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) {
    throw new Error(
      status === 'verified'
        ? 'Verification did not save. You may lack verifier permission, or this application is no longer pending review.'
        : 'Update did not save. You may lack permission for this status, or the application changed.',
    );
  }

  if (status === 'verified' || status === 'approved' || status === 'rejected') {
    const row = await fetchAccountCreationSubmission(id);
    if (row) {
      const event =
        status === 'verified' ? 'verified' : status === 'approved' ? 'approved' : 'rejected';
      void notifyApplicationFormEvent(row, event);
    }
  }
}

export async function queueAccountCreationSharePointSync(submissionId: string): Promise<void> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) return;

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  try {
    await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ action: 'sync_submission', submission_id: submissionId }),
    });
  } catch (err) {
    console.warn('SharePoint sync queue failed:', err);
  }
}
